import {
  Currency,
  type GetSavingQuery,
  type SavingCostAllocationInput,
  SavingInput,
  SavingType,
  SourceReference,
  SourceReferenceInput,
} from '@/generated/gql/graphql';
import { coaleseDecimal } from '~/util/number/coaleseDecimal';

import { savingFormDefaultValues, SavingFormData } from './components/SavingForm/SavingForm.schema';

type ExistingSourceReference = Pick<
  SourceReference,
  'fkSourceSystem' | 'isPrimary' | 'sourceContext' | 'sourceReference' | 'sourceReferenceItem'
>;

export const SOURCE_CONTEXT_EXTERNAL_NUMBER = 'external-number';
export const SOURCE_CONTEXT_SHOPPING_CART = 'shopping-cart';

// sourceContext is a JSON string, e.g. '{"type":"external-number"}' (deltax emits it
// compact, merger re-serialises it space-formatted). Parse it and read `.type` rather
// than comparing the raw string, which would never match either form.
const parseSourceContextType = (sourceContext?: string | null): string | null => {
  if (!sourceContext) {
    return null;
  }
  try {
    return (JSON.parse(sourceContext) as { type?: string })?.type ?? null;
  } catch {
    return null;
  }
};

export const getSourceReferenceCode = (
  sourceReferences: Pick<SourceReference, 'sourceContext' | 'sourceReference'>[],
  contextType: string,
): string | null =>
  sourceReferences.find((reference) => parseSourceContextType(reference.sourceContext) === contextType)
    ?.sourceReference ?? null;

// The line-item id (sourceReferenceItem) is the same on both the external-number and
// shopping-cart references, so we surface it once.
export const getSourceReferenceItem = (
  sourceReferences: Pick<SourceReference, 'sourceContext' | 'sourceReferenceItem'>[],
  contextType: string,
): string | null =>
  sourceReferences.find((reference) => parseSourceContextType(reference.sourceContext) === contextType)
    ?.sourceReferenceItem ?? null;

const toCostAllocationInput = (data: SavingFormData): SavingCostAllocationInput | null => {
  if (!data.distributionType) {
    return null;
  }

  return {
    distributionType: data.distributionType,
    allocationValue: data.allocationValue != null ? data.allocationValue.toString() : null,
    fkGeneralLedgerAccount: data.glAccountId,
    fkAccountAssignmentCategory: data.accountAssignmentCategory,
    fkAccountAssignment: data.accountAssignmentValue,
  };
};

export const buildSourceReferencesInput = (
  existingSourceReferences: ExistingSourceReference[] = [],
): SourceReferenceInput[] =>
  existingSourceReferences.map(
    ({ fkSourceSystem, isPrimary, sourceContext, sourceReference, sourceReferenceItem }) => ({
      fkSourceSystem,
      isPrimary,
      sourceContext,
      sourceReference,
      sourceReferenceItem,
    }),
  );

export const mapSavingFormDataToInput = (
  data: SavingFormData,
  existingSourceReferences: ExistingSourceReference[] = [],
): SavingInput => {
  const visibleCostAllocation = toCostAllocationInput(data);
  const costAllocation = visibleCostAllocation ? [visibleCostAllocation] : undefined;

  return {
    savingType: SavingType.MANUAL,
    status: data.status,
    name: data.name.trim(),
    description: data.description.trim(),
    currency: data.currency as Currency,
    baselinePrice: data.baselinePrice,
    newPrice: data.newPrice,
    baselineQuantity: data.baselineQuantity,
    newQuantity: data.newQuantity,
    originalPriceQuantity: data.originalPriceQuantity,
    newPriceQuantity: data.newPriceQuantity,
    validFrom: data.validFrom,
    validTo: data.validTo,
    fkProcurementOrganization: data.procurementOrganizationId,
    fkOrganization: data.organizationId,
    fkCommodityGroup: data.commodityGroupId,
    freeTextCommodity: data.commodity.trim(),
    fkPurchasingGroup: data.purchasingGroupId,
    fkSupplier: data.supplierId,
    fkAccountingArea: data.accountingAreaId,
    fkBuyer: data.fkBuyer,
    fkUnit: data.unitId,
    reductionType: data.reductionType,
    costAllocation,
    sourceReferences: buildSourceReferencesInput(existingSourceReferences),
  };
};

export const mapSavingToFormData = (saving: NonNullable<GetSavingQuery['saving']>): SavingFormData => {
  const [firstCostAllocation] = saving.costAllocation;

  return {
    purchaseRequisition: getSourceReferenceCode(saving.sourceReferences, SOURCE_CONTEXT_EXTERNAL_NUMBER) ?? '',
    shoppingCartId: getSourceReferenceCode(saving.sourceReferences, SOURCE_CONTEXT_SHOPPING_CART) ?? '',
    sourceReferenceItem:
      getSourceReferenceItem(saving.sourceReferences, SOURCE_CONTEXT_EXTERNAL_NUMBER) ??
      getSourceReferenceItem(saving.sourceReferences, SOURCE_CONTEXT_SHOPPING_CART) ??
      '',
    name: saving.name ?? '',
    description: saving.description ?? '',
    currency: saving.currency ?? '',
    status: saving.status,
    savingType: saving.savingType ?? null,
    baselinePrice: coaleseDecimal(saving.baselinePrice),
    newPrice: coaleseDecimal(saving.newPrice),
    baselineQuantity: coaleseDecimal(saving.baselineQuantity),
    newQuantity: coaleseDecimal(saving.newQuantity),
    originalPriceQuantity:
      saving.originalPriceQuantity == null ? undefined : coaleseDecimal(saving.originalPriceQuantity),
    newPriceQuantity: saving.newPriceQuantity == null ? undefined : coaleseDecimal(saving.newPriceQuantity),
    validFrom: saving.validFrom,
    validTo: saving.validTo ?? savingFormDefaultValues.validTo ?? null,
    procurementOrganizationId: saving.fkProcurementOrganization ?? '',
    organizationId: saving.fkOrganization ?? '',
    commodityGroupId: saving.fkCommodityGroup ?? '',
    commodity: saving.freeTextCommodity ?? '',
    purchasingGroupId: saving.fkPurchasingGroup ?? '',
    supplierId: saving.fkSupplier ?? '',
    accountingAreaId: saving.fkAccountingArea ?? null,
    fkBuyer: saving.fkBuyer ?? null,
    unitId: saving.fkUnit ?? null,
    glAccountId: firstCostAllocation?.fkGeneralLedgerAccount ?? null,
    accountAssignmentCategory: firstCostAllocation?.fkAccountAssignmentCategory ?? null,
    accountAssignmentValue: firstCostAllocation?.fkAccountAssignment ?? null,
    distributionType: firstCostAllocation?.distributionType ?? null,
    allocationValue:
      firstCostAllocation?.allocationValue == null ? undefined : coaleseDecimal(firstCostAllocation.allocationValue),
    reductionType: saving.reductionType ?? null,
  };
};
